import React, { useEffect, useState } from "react";
import { callApi } from "../../../ApiHelper/ApiHelper";
import { apiUrl } from "../../../ApiHelper/ApiUrl";
import KpiStatCard from "../components/KpiStatCard";
import QuickServiceModal from "../components/QuickServiceModal";
import moment from "moment";
import { safeText } from "../utils/hubHelpers";

export default function MaintenanceExpiry() {
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedMachineForService, setSelectedMachineForService] = useState(null);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const res = await callApi(apiUrl.get_maintenance_dashboard, { method: "GET" });
      if (res?.status === true) {
        setDashboardData(res?.data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const counts = dashboardData?.counts || {};
  const expiredDocsList = Array.isArray(dashboardData?.documentExpiry)
    ? dashboardData.documentExpiry
    : Array.isArray(dashboardData?.documentExpiryList)
    ? dashboardData.documentExpiryList
    : [];
  const serviceDueList = Array.isArray(dashboardData?.serviceDue)
    ? dashboardData.serviceDue
    : Array.isArray(dashboardData?.serviceDueList)
    ? dashboardData.serviceDueList
    : [];

  const hasCriticalExpiries = Number(counts?.expiredDocs || 0) > 0;

  return (
    <div>
      {/* 1. Critical Warning Banner (Screenshot 4) */}
      {hasCriticalExpiries && (
        <div className="compliance-alert-banner">
          <span style={{ fontSize: "1.25rem" }}>⚠️</span>
          <div>
            <strong>{counts.expiredDocs} documents have expired!</strong> Vehicles and machinery cannot be deployed to field sites without valid regulatory papers — renew immediately.
          </div>
        </div>
      )}

      {/* 2. Compliance KPI Cards */}
      <div className="kpi-grid">
        <KpiStatCard
          label="Expired Documents"
          value={counts?.expiredDocs || 0}
          subtext="Action required immediately"
          icon={<span>📄</span>}
          color="#ef4444"
          iconBg="#fef2f2"
        />

        <KpiStatCard
          label="Expiring Soon"
          value={counts?.expiringSoonDocs || 0}
          subtext="Within next 45 days"
          icon={<span>⏱️</span>}
          color="#f59e0b"
          iconBg="#fffbeb"
        />

        <KpiStatCard
          label="Service Overdue / Due"
          value={counts?.serviceDue || 0}
          subtext="Within next 45 days"
          icon={<span>🔧</span>}
          color="#3b82f6"
          iconBg="#eff6ff"
        />

        <KpiStatCard
          label="Assets Down"
          value={counts?.assetsDown || 0}
          subtext="In breakdown or overhaul"
          icon={<span>🔴</span>}
          color="#dc2626"
          iconBg="#fee2e2"
        />
      </div>

      {/* 3. Split 2-Column Desk (Screenshot 4) */}
      <div className="split-desk-container">
        {/* Left Column: Document Expiry Compliance */}
        <div className="desk-card">
          <div className="desk-card-header">
            <div>
              <h4 className="desk-title">📄 Document Expiry Compliance</h4>
              <p className="desk-subtitle">Next 45 days & overdue compliance papers</p>
            </div>
            <span style={{ fontSize: "12px", background: "#f1f5f9", padding: "3px 9px", borderRadius: 10, fontWeight: 600 }}>
              {expiredDocsList.length} records
            </span>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table className="desk-table">
              <thead>
                <tr>
                  <th>Asset / Machine</th>
                  <th>Document Type</th>
                  <th>Expiry Date</th>
                  <th>Compliance Status</th>
                  <th style={{ textAlign: "right" }}>File</th>
                </tr>
              </thead>
              <tbody>
                {expiredDocsList.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center", padding: "2rem", color: "#94a3b8", fontSize: "13px" }}>
                      ✓ All documents are up-to-date and compliant.
                    </td>
                  </tr>
                ) : (
                  expiredDocsList.map((doc) => {
                    const expiry = doc.expiryDate ? moment(doc.expiryDate) : null;
                    const today = moment();
                    const diffDays = expiry ? expiry.diff(today, "days") : 0;
                    const isExpired = diffDays < 0;

                    return (
                      <tr key={doc?.id || Math.random()}>
                        <td style={{ textAlign: "left" }}>
                          <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "13.5px", textAlign: "left" }}>
                            {safeText(doc?.Machinery?.name || doc?.name, "Equipment")}
                          </div>
                          <div style={{ fontSize: "12px", color: "#64748b", textAlign: "left" }}>
                            {safeText(doc?.Machinery?.machineNumber || doc?.Machinery?.assetNo, "—")}
                          </div>
                        </td>
                        <td style={{ fontWeight: 600, color: "#334155", fontSize: "13px", textAlign: "left" }}>
                          {safeText(doc?.type || doc?.name, "Compliance Doc")}
                        </td>
                        <td style={{ color: "#475569", fontSize: "13px", textAlign: "left" }}>
                          {expiry ? expiry.format("DD MMM YYYY") : "—"}
                        </td>
                        <td style={{ textAlign: "left" }}>
                          {isExpired ? (
                            <span style={{ color: "#dc2626", background: "#fee2e2", padding: "3px 8px", borderRadius: 4, fontWeight: 700, fontSize: "12px" }}>
                              Expired {Math.abs(diffDays)}d ago
                            </span>
                          ) : (
                            <span style={{ color: "#b45309", background: "#fef3c7", padding: "3px 8px", borderRadius: 4, fontWeight: 600, fontSize: "12px" }}>
                              Expires in {diffDays}d
                            </span>
                          )}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {doc.url ? (
                            <a
                              href={doc.url}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                padding: "4px 9px",
                                fontSize: "12px",
                                background: "#0f172a",
                                color: "#fff",
                                borderRadius: 4,
                                textDecoration: "none",
                                fontWeight: 600,
                              }}
                            >
                              View ↗
                            </a>
                          ) : (
                            <span style={{ color: "#94a3b8", fontSize: "12px" }}>No file</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right Column: Preventive Service Due */}
        <div className="desk-card">
          <div className="desk-card-header">
            <div>
              <h4 className="desk-title">🔧 Preventive Maintenance Desk</h4>
              <p className="desk-subtitle">Next 45 days & overdue scheduled services</p>
            </div>
            <span style={{ fontSize: "12px", background: "#f1f5f9", padding: "3px 9px", borderRadius: 10, fontWeight: 600 }}>
              {serviceDueList.length} machines
            </span>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table className="desk-table">
              <thead>
                <tr>
                  <th>Asset / Machine</th>
                  <th>Due Date</th>
                  <th>Service Status</th>
                  <th style={{ textAlign: "right" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {serviceDueList.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: "center", padding: "2rem", color: "#94a3b8", fontSize: "13px" }}>
                      ✓ No services due within next 45 days.
                    </td>
                  </tr>
                ) : (
                  serviceDueList.map((m) => {
                    const dueDate = m.nextServiceDate ? moment(m.nextServiceDate) : null;
                    const today = moment();
                    const diffDays = dueDate ? dueDate.diff(today, "days") : 0;
                    const isOverdue = diffDays < 0;

                    return (
                      <tr key={m?.id || Math.random()}>
                        <td style={{ textAlign: "left" }}>
                          <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "13.5px", textAlign: "left" }}>{safeText(m?.name, "Machine")}</div>
                          <div style={{ fontSize: "12px", color: "#64748b", textAlign: "left" }}>
                            {safeText(m?.assetNo, "—")} • {safeText(m?.machineNumber, "—")}
                          </div>
                        </td>
                        <td style={{ color: "#334155", fontSize: "13px", textAlign: "left" }}>
                          {dueDate ? dueDate.format("DD MMM YYYY") : "—"}
                        </td>
                        <td style={{ textAlign: "left" }}>
                          {isOverdue ? (
                            <span style={{ color: "#dc2626", background: "#fee2e2", padding: "3px 8px", borderRadius: 4, fontWeight: 700, fontSize: "12px" }}>
                              {Math.abs(diffDays)}d overdue
                            </span>
                          ) : (
                            <span style={{ color: "#2563eb", background: "#eff6ff", padding: "3px 8px", borderRadius: 4, fontWeight: 600, fontSize: "12px" }}>
                              Due in {diffDays}d
                            </span>
                          )}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedMachineForService(m);
                              setIsServiceModalOpen(true);
                            }}
                            style={{
                              padding: "4px 10px",
                              fontSize: "12px",
                              fontWeight: 600,
                              background: "#059669",
                              color: "#fff",
                              border: "none",
                              borderRadius: 4,
                              cursor: "pointer",
                            }}
                          >
                            + Log Service
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Service Modal */}
      <QuickServiceModal
        isOpen={isServiceModalOpen}
        toggle={() => setIsServiceModalOpen(!isServiceModalOpen)}
        machine={selectedMachineForService}
        onSuccess={fetchDashboardData}
      />
    </div>
  );
}
